import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Plus, Upload, ImageIcon, X, Video, Compass } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UnitCard = ({ unit, initial, onSaved }) => {
  const [description, setDescription] = useState(initial?.description || "");
  const [images, setImages] = useState(initial?.images || []);
  const [videoUrl, setVideoUrl] = useState(initial?.video_url || "");
  const [panoramaUrl, setPanoramaUrl] = useState(initial?.panorama_url || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPano, setUploadingPano] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  const upload = async (files) => {
    // Accept single file (from single-select) or FileList/array (from multi-select).
    const list = files instanceof File ? [files] : Array.from(files || []);
    if (list.length === 0) return;
    const remainingSlots = Math.max(0, 40 - images.length);
    if (remainingSlots === 0) {
      toast.error("Photo limit reached (40). Remove some first.");
      return;
    }
    const toUpload = list.slice(0, remainingSlots);
    if (toUpload.length < list.length) {
      toast.warning(`Only uploading ${toUpload.length} of ${list.length} — cap is 40 photos per pitch.`);
    }
    setUploading(true);
    let okCount = 0;
    for (let i = 0; i < toUpload.length; i++) {
      const file = toUpload[i];
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await axios.post(`${API}/admin/upload`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        });
        const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setImages((imgs) => [...imgs, url]);
        okCount++;
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(`${file.name}: ${typeof detail === "string" ? detail : "upload failed"}`);
      }
    }
    if (okCount > 0) toast.success(`${okCount} image${okCount > 1 ? "s" : ""} uploaded.`);
    setUploading(false);
  };

  const removeImage = (idx) => setImages((imgs) => imgs.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/unit-media/${unit.id}`, {
        description: description.trim(),
        images,
        video_url: videoUrl.trim(),
        panorama_url: panoramaUrl.trim(),
      });
      toast.success(`Saved · ${unit.id}`);
      onSaved?.();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const uploadPano = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploadingPano(true);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setPanoramaUrl(url);
      toast.success("360° image uploaded — remember to Save.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploadingPano(false);
    }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploadingVideo(true);
    setVideoProgress(0);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        onUploadProgress: (evt) => {
          if (evt.total) setVideoProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setVideoUrl(url);
      toast.success("Video uploaded — remember to Save.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Video upload failed.");
    } finally {
      setUploadingVideo(false);
      setVideoProgress(0);
    }
  };

  const clear = async () => {
    if (!window.confirm(`Clear all media for ${unit.id}?`)) return;
    try {
      await axios.delete(`${API}/admin/unit-media/${unit.id}`);
      setDescription("");
      setImages([]);
      setVideoUrl("");
      setPanoramaUrl("");
      toast.success("Cleared.");
      onSaved?.();
    } catch {
      toast.error("Could not clear.");
    }
  };

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-4 md:p-5" data-testid={`unit-media-${unit.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-serif text-lg text-[#14100F]">{unit.name}</p>
            {(initial?.images || []).length === 0 && (
              <span className="px-2 py-0.5 rounded-full bg-[#B76E79]/10 text-[#B76E79] text-[9px] uppercase tracking-[0.14em] font-semibold" data-testid={`unit-media-needs-${unit.id}`}>Needs photos</span>
            )}
          </div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58]">ID: {unit.id} · £{unit.nightly}/nt</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={clear}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] text-[#B76E79] hover:text-[#8f4a52]"
            data-testid={`unit-media-clear-${unit.id}`}
          >
            <Trash2 size={13} /> Clear
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition-colors text-xs uppercase tracking-[0.14em] disabled:opacity-60"
            data-testid={`unit-media-save-${unit.id}`}
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58]">Description shown to guests</Label>
          <Textarea
            className="mt-1 min-h-[100px]"
            placeholder="A quiet pitch by the lake with morning sun. Water tap 15m away…"
            value={description}
            maxLength={800}
            onChange={(e) => setDescription(e.target.value)}
            data-testid={`unit-media-desc-${unit.id}`}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58]">Video URL (YouTube · Vimeo · MP4)</Label>
          <div className="mt-1 flex items-center gap-2">
            <Video size={16} className="text-[#6A5F58]" strokeWidth={1.7} />
            <Input
              placeholder="https://youtu.be/…  or  https://cdn.example.com/pitch.mp4"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              maxLength={500}
              data-testid={`unit-media-video-${unit.id}`}
            />
          </div>
          <div className="mt-2 flex items-center gap-3">
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-dashed border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white transition-colors cursor-pointer text-xs uppercase tracking-[0.14em]" data-testid={`unit-media-video-upload-${unit.id}`}>
              {uploadingVideo ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} strokeWidth={2} />}
              {uploadingVideo ? `Uploading ${videoProgress}%` : (videoUrl ? "Replace video file" : "Upload video file")}
              <input
                type="file"
                accept="video/mp4,video/webm,video/quicktime,video/x-matroska"
                className="hidden"
                onChange={(e) => uploadVideo(e.target.files?.[0])}
                disabled={uploadingVideo}
              />
            </label>
            {videoUrl && (
              <button
                type="button"
                onClick={() => setVideoUrl("")}
                className="text-[11px] text-[#B76E79] hover:text-[#8f4a52] uppercase tracking-[0.14em]"
                data-testid={`unit-media-video-clear-${unit.id}`}
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-2 text-[11px] text-[#6A5F58]">Paste a YouTube/Vimeo link, or upload an MP4/WebM/MOV directly (any size).</p>
        </div>
      </div>

      <div className="mt-5">
        <Label className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58] flex items-center gap-2">
          <Compass size={13} className="text-[#B76E79]" strokeWidth={2} /> 360° panorama (equirectangular JPG, 2:1 ratio)
        </Label>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {panoramaUrl && (
            <div className="relative w-44 h-20 rounded-lg overflow-hidden border border-[#B76E79]" data-testid={`unit-media-pano-thumb-${unit.id}`}>
              <img src={panoramaUrl} alt="" className="w-full h-full object-cover" />
              <span className="absolute top-1 left-1 bg-[#B76E79] text-white text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded">360°</span>
              <button
                type="button"
                onClick={() => setPanoramaUrl("")}
                className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-red-600"
                aria-label="Remove 360 image"
                data-testid={`unit-media-pano-remove-${unit.id}`}
              >
                <X size={12} />
              </button>
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-dashed border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white transition-colors cursor-pointer text-xs uppercase tracking-[0.14em]" data-testid={`unit-media-pano-add-${unit.id}`}>
            {uploadingPano ? <Loader2 size={13} className="animate-spin" /> : <Compass size={13} strokeWidth={2} />}
            {panoramaUrl ? "Replace 360°" : "Add 360° panorama"}
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              className="hidden"
              onChange={(e) => uploadPano(e.target.files?.[0])}
              disabled={uploadingPano}
            />
          </label>
          <span className="text-[11px] text-[#6A5F58]">
            One 360° image per pitch. Any equirectangular photo works — iPhone Pano, GoPro Max, Insta360, Google Street View.
          </span>
        </div>
      </div>

      <div className="mt-5">
        <Label className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58]">Photos ({images.length}/40)</Label>
        <div className="mt-2 flex flex-wrap gap-3">
          {images.map((url, i) => (
            <div key={`${url}-${i}`} className="relative w-28 h-20 rounded-lg overflow-hidden border border-[#DDD1BE]" data-testid={`unit-media-thumb-${unit.id}-${i}`}>
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-red-600"
                data-testid={`unit-media-thumb-remove-${unit.id}-${i}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {images.length < 40 && (
            <label className="w-28 h-20 rounded-lg border-2 border-dashed border-[#DDD1BE] flex flex-col items-center justify-center cursor-pointer hover:border-[#B76E79] hover:text-[#B76E79] text-[#6A5F58] text-[9px] uppercase tracking-[0.14em] gap-0.5" data-testid={`unit-media-add-${unit.id}`}>
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <><Plus size={18} /><span>Add photos</span></>}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
                disabled={uploading}
                data-testid={`unit-media-add-input-${unit.id}`}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminPitchMedia = () => {
  const [types, setTypes] = useState([]);
  const [mediaByUnit, setMediaByUnit] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([
        axios.get(`${API}/units`),
        axios.get(`${API}/admin/unit-media`),
      ]);
      setTypes(u.data?.types || []);
      const map = {};
      for (const row of m.data || []) map[row.unit_id] = row;
      setMediaByUnit(map);
    } catch {
      toast.error("Could not load units.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const unitsFlat = useMemo(() => {
    return types.flatMap((t) => (t.units || []).map((u) => ({ ...u, category: t.type })));
  }, [types]);

  const withPhotos = unitsFlat.filter((u) => (mediaByUnit[u.id]?.images || []).length > 0);
  const withPano = unitsFlat.filter((u) => !!mediaByUnit[u.id]?.panorama_url);

  const visible = useMemo(() => {
    if (filter === "all") return unitsFlat;
    if (filter === "lodge") return unitsFlat.filter((u) => u.category === "lodge");
    if (filter === "needs-photo") return unitsFlat.filter((u) => (mediaByUnit[u.id]?.images || []).length === 0);
    if (filter === "daisy-hs") return unitsFlat.filter((u) => u.id.startsWith("daisy-hs-"));
    if (filter === "walnut-hs") return unitsFlat.filter((u) => u.id.startsWith("walnut-hs-"));
    if (filter === "walnut-grass") return unitsFlat.filter((u) => u.id.startsWith("walnut-grass-"));
    if (filter === "walnut-tent") return unitsFlat.filter((u) => u.id.startsWith("walnut-tent-"));
    return unitsFlat;
  }, [unitsFlat, filter, mediaByUnit]);

  const filters = [
    { id: "all", label: `All (${unitsFlat.length})` },
    { id: "needs-photo", label: `Needs photos (${unitsFlat.length - withPhotos.length})` },
    { id: "lodge", label: `Lodges (${unitsFlat.filter(u => u.category === "lodge").length})` },
    { id: "daisy-hs", label: `Daisy · Hardstanding (${unitsFlat.filter(u => u.id.startsWith("daisy-hs-")).length})` },
    { id: "walnut-hs", label: `Walnut · Hardstanding (${unitsFlat.filter(u => u.id.startsWith("walnut-hs-")).length})` },
    { id: "walnut-grass", label: `Walnut · Grass EHU (${unitsFlat.filter(u => u.id.startsWith("walnut-grass-")).length})` },
    { id: "walnut-tent", label: `Walnut · Tent (${unitsFlat.filter(u => u.id.startsWith("walnut-tent-")).length})` },
  ];

  return (
    <div className="space-y-6" data-testid="admin-pitch-media">
      <div>
        <p className="overline text-[#6A5F58]"><span className="marker-line" /> Pitch media</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#14100F] mt-2">Photos, videos & descriptions</h1>
        <p className="mt-2 text-sm text-[#6A5F58] max-w-2xl">
          Upload photos, add a video tour and describe each pitch or lodge. Guests see this on the booking form when they select the unit — so they know exactly what they&apos;re booking.
        </p>
      </div>

      {/* Photo coverage checklist — how many pitches have photos / 360° yet */}
      <div className="bg-white border border-[#DDD1BE] rounded-2xl p-4 md:p-5" data-testid="pitch-media-progress">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="flex items-baseline justify-between">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58]">Pitches with photos</p>
              <p className="font-serif text-xl text-[#14100F]" data-testid="photos-count">{withPhotos.length} <span className="text-sm text-[#6A5F58]">/ {unitsFlat.length}</span></p>
            </div>
            <div className="mt-2 h-2 rounded-full bg-[#F5EFE6] overflow-hidden">
              <div className="h-full rounded-full bg-[#3B8C4E] transition-all" style={{ width: `${unitsFlat.length ? (withPhotos.length / unitsFlat.length) * 100 : 0}%` }} data-testid="photos-bar" />
            </div>
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58]">Pitches with 360° view</p>
              <p className="font-serif text-xl text-[#14100F]" data-testid="pano-count">{withPano.length} <span className="text-sm text-[#6A5F58]">/ {unitsFlat.length}</span></p>
            </div>
            <div className="mt-2 h-2 rounded-full bg-[#F5EFE6] overflow-hidden">
              <div className="h-full rounded-full bg-[#B76E79] transition-all" style={{ width: `${unitsFlat.length ? (withPano.length / unitsFlat.length) * 100 : 0}%` }} data-testid="pano-bar" />
            </div>
          </div>
        </div>
        {withPhotos.length < unitsFlat.length && (
          <p className="mt-3 text-xs text-[#6A5F58]">
            Tip: tap <strong>Needs photos</strong> below to work through just the pitches still missing a photo.
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="pitch-media-filters">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-[0.14em] transition ${
              filter === f.id
                ? "bg-[#0F0E0E] text-[#F5EFE6] border-[#0F0E0E]"
                : "bg-white text-[#3A322E] border-[#DDD1BE] hover:border-[#B76E79]"
            }`}
            data-testid={`pitch-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-center py-12 text-[#6A5F58]">
          <Loader2 className="mx-auto animate-spin" />
        </div>
      )}

      <div className="space-y-4">
        {visible.map((u) => (
          <UnitCard key={u.id} unit={u} initial={mediaByUnit[u.id]} onSaved={load} />
        ))}
      </div>
    </div>
  );
};

export default AdminPitchMedia;
